import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Modal, Field, ModalFooter } from "./Contacts";
import { Plus, Trash, Funnel } from "@phosphor-icons/react";
import { toast } from "sonner";

const FIELDS = [
  { value: "tag", label: "Has tag", ops: ["eq", "neq"] },
  { value: "lifecycle_stage", label: "Lifecycle stage", ops: ["eq", "neq"] },
  { value: "source", label: "Source", ops: ["eq", "neq", "contains"] },
  { value: "company", label: "Company", ops: ["eq", "contains"] },
  { value: "status", label: "Status", ops: ["eq", "neq"] },
  { value: "last_opened_days", label: "Last opened (days)", ops: ["within", "gt"] },
  { value: "engagement_score", label: "Engagement score", ops: ["gte", "gt", "lte", "lt"] },
];

export default function SegmentsPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const load = () => api.listSegments().then(setItems);
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-medium tracking-tight">Segments</h2>
          <p className="text-sm text-zinc-500 mt-0.5">Rule-based, auto-updating audiences.</p>
        </div>
        <button data-testid="btn-new-segment" onClick={() => setOpen(true)} className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-zinc-950 text-white hover:bg-zinc-800"><Plus size={14}/> New segment</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map(s => (
          <div key={s.id} className="rounded-lg border border-zinc-200 bg-white p-4">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded bg-zinc-100 flex items-center justify-center"><Funnel size={14}/></div>
                <div>
                  <div className="font-medium text-zinc-900" data-testid={`seg-${s.name}`}>{s.name}</div>
                  <div className="text-[11px] text-zinc-500">{s.rules.length} rule(s) · match {s.match}</div>
                </div>
              </div>
              <button onClick={async () => { if(window.confirm("Delete?")) { await api.deleteSegment(s.id); load(); }}} className="text-zinc-400 hover:text-rose-700"><Trash size={14}/></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="col-span-full border border-dashed border-zinc-200 rounded-lg p-10 text-center text-sm text-zinc-500">No segments yet</div>}
      </div>

      {open && <NewSegmentModal onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }}/>}
    </div>
  );
}

function NewSegmentModal({ onClose, onSaved }) {
  const [name, setName] = useState("");
  const [match, setMatch] = useState("all");
  const [rules, setRules] = useState([{ field: "tag", op: "eq", value: "" }]);
  const [preview, setPreview] = useState(null);
  const [tagOpts, setTagOpts] = useState([]);
  useEffect(() => { api.listTags().then(d => setTagOpts(d.items || [])).catch(() => {}); }, []);

  const update = (i, k, v) => { const next = [...rules]; next[i] = {...next[i], [k]: v}; setRules(next); };
  const addRule = () => setRules([...rules, { field: "tag", op: "eq", value: "" }]);
  const removeRule = (i) => setRules(rules.filter((_, idx) => idx !== i));

  const doPreview = async () => {
    const data = await api.previewSegment({ name: name || "preview", rules, match });
    setPreview(data);
  };
  const save = async () => {
    await api.createSegment({ name, description: "", rules, match });
    toast.success("Segment created");
    onSaved();
  };

  return (
    <Modal title="New segment" onClose={onClose} wide>
      <Field label="Name"><input data-testid="seg-name" value={name} onChange={(e) => setName(e.target.value)} className="modal-input"/></Field>
      <div className="mt-3 flex items-center gap-2 text-sm">
        <span>Match</span>
        <select data-testid="seg-match" value={match} onChange={(e) => setMatch(e.target.value)} className="modal-input w-32">
          <option value="all">All</option><option value="any">Any</option>
        </select>
        <span>of the following rules:</span>
      </div>
      <div className="mt-3 space-y-2">
        {rules.map((r, i) => {
          const def = FIELDS.find(f => f.value === r.field);
          return (
            <div key={i} className="flex items-center gap-2" data-testid={`rule-${i}`}>
              <select value={r.field} onChange={(e) => update(i, "field", e.target.value)} className="modal-input">
                {FIELDS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
              <select value={r.op} onChange={(e) => update(i, "op", e.target.value)} className="modal-input w-32">
                {(def?.ops || ["eq"]).map(o => <option key={o} value={o}>{o}</option>)}
              </select>
              <input value={r.value} onChange={(e) => update(i, "value", e.target.value)} className="modal-input" placeholder="Value" list={r.field === "tag" ? "seg-tag-suggestions" : undefined}/>
              <button onClick={() => removeRule(i)} className="p-1 text-zinc-400 hover:text-rose-700"><Trash size={14}/></button>
            </div>
          );
        })}
        <button onClick={addRule} className="text-xs text-zinc-600 hover:text-zinc-950 inline-flex items-center gap-1"><Plus size={12}/> Add rule</button>
        <datalist id="seg-tag-suggestions">{tagOpts.map(t => <option key={t.name} value={t.name} />)}</datalist>
      </div>
      <div className="mt-4 flex items-center gap-3">
        <button data-testid="seg-preview" onClick={doPreview} className="text-sm px-3 py-1.5 rounded-md border border-zinc-200 hover:bg-zinc-50">Preview</button>
        {preview && <div className="text-sm text-zinc-700"><span className="font-medium">{preview.count}</span> matching contacts</div>}
      </div>
      <ModalFooter onClose={onClose}>
        <button data-testid="seg-save" disabled={!name} onClick={save} className="modal-btn-primary">Save segment</button>
      </ModalFooter>
    </Modal>
  );
}
